import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";

export const OrderFeedbackForm = ({ feedback, onSubmitted, testIdPrefix }) => {
  const [reviews, setReviews] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!feedback?.items) return;
    setReviews(Object.fromEntries(feedback.items.map((item) => [
      item.line_key,
      { stars: 0, review: "" },
    ])));
  }, [feedback]);

  if (!feedback?.eligible) return null;

  const updateReview = (lineKey, patch) => {
    setReviews((current) => ({
      ...current,
      [lineKey]: { ...current[lineKey], ...patch },
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (feedback.items.some((item) => !reviews[item.line_key]?.stars)) {
      toast.error("Please choose a rating for every product.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/feedback/submit", {
        token: feedback.token,
        reviews: feedback.items.map((item) => ({
          line_key: item.line_key,
          stars: reviews[item.line_key].stars,
          review: reviews[item.line_key].review,
        })),
      });
      onSubmitted?.(data);
      toast.success("Thank you for your product feedback.");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="space-y-5" onSubmit={submit} data-testid={`${testIdPrefix}-feedback-form`}>
      {feedback.items.map((item) => {
        const current = reviews[item.line_key] || { stars: 0, review: "" };
        return (
          <section
            key={item.line_key}
            className="border-t border-[#E2DCD2] pt-5 first:border-t-0 first:pt-0"
            data-testid={`${testIdPrefix}-feedback-item-${item.line_key}`}
          >
            <div className="flex gap-3">
              {item.image && (
                <img
                  src={item.image}
                  alt={item.name}
                  className="h-14 w-14 border border-[#E2DCD2] object-cover"
                />
              )}
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-[#1E2A24]">{item.name}</h3>
                <p className="mt-1 text-xs text-[#64748B]">
                  {item.variant_label || "Standard"} · Quantity {item.quantity}
                </p>
                <div className="mt-3 flex gap-1" data-testid={`${testIdPrefix}-feedback-stars-${item.line_key}`}>
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      aria-label={`${item.name}: ${value} stars`}
                      onClick={() => updateReview(item.line_key, { stars: value })}
                      className="p-0.5 text-[#D99B26] transition-transform hover:scale-110"
                      data-testid={`${testIdPrefix}-feedback-star-${item.line_key}-${value}`}
                    >
                      <Star size={22} fill={value <= current.stars ? "currentColor" : "none"} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <textarea
              value={current.review}
              maxLength={2000}
              onChange={(event) => updateReview(item.line_key, { review: event.target.value })}
              rows={2}
              className="mt-3 w-full border border-[#E2DCD2] px-3 py-2.5 text-sm outline-none focus:border-[#2D5A47]"
              placeholder={`Write an optional review for ${item.name}`}
              data-testid={`${testIdPrefix}-feedback-review-${item.line_key}`}
            />
          </section>
        );
      })}
      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#C86D42] px-5 py-3 text-sm font-semibold text-white disabled:opacity-50"
        data-testid={`${testIdPrefix}-submit-feedback`}
      >
        {submitting ? "Submitting feedback…" : "Submit product feedback"}
      </button>
    </form>
  );
};